import { type Joi, Koi } from '@ksyos/koi';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums/organizationType';
import type { Profession } from '@common/enums/profession';
import {
    agbCodeSchema,
    identifierRegistrationBigBackendSchema,
    identifierRegistrationBigFrontendSchema,
    identifierRegistrationKpSchema,
} from '../schemas/identifierDeclarationSchemas';
import { zorgMailNummerSchema } from '../schemas/zorgMailNummerSchema';
import {
    identifierDeclarationOrSupervisorRequired,
    identifierDeclarationOrSupervisorRequiredByOrganizationType,
    identifierDeclarationRequired,
    identifierDeclarationRequiredByOrganizationType,
    identifierRegistrationBigAllowed,
    identifierRegistrationBigAllowedByOrganizationType,
    identifierRegistrationKpAllowed,
    identifierRegistrationKpRequiredByOrganizationType,
    professionSpecialismCodes,
    specialismRequired,
    supervisorRequired,
    supervisorRequiredByOrganizationType,
    zorgmailNummerRequired,
    zorgmailNummerRequiredByOrganizationType,
} from './accountFieldsRequirementsByProfession';

export const specialismCodeValidator = Koi.custom(
    (value, helpers) => validateSpecialismCodeField(value, helpers),
    'specialismCode custom validation'
);

export function validateIdentifierDeclarationPractitionerIdField(value: string | null, helpers: Joi.CustomHelpers) {
    const identifierDeclaration: string | null =
        helpers.state.ancestors[1]?.practitioner?.identifierDeclaration ??
        helpers.state.ancestors[1]?.practitionerRegistrationCodes?.identifierDeclaration;

    const organizationType: OrganizationTypeWithLinkedProfessions | null = helpers.state.ancestors[1]?.organizationType;
    const profession = helpers.state.ancestors[0]?.profession as Profession;

    const supervisorRequiredProfessions = organizationType
        ? (supervisorRequiredByOrganizationType[organizationType] as Profession[])
        : supervisorRequired;

    const identifierDeclarationOrSupervisorRequiredProfessions = organizationType
        ? (identifierDeclarationOrSupervisorRequiredByOrganizationType[organizationType] as Profession[])
        : identifierDeclarationOrSupervisorRequired;

    if (Object.values(supervisorRequiredProfessions).includes(profession)) {
        return value ?? helpers.error('any.selected');
    } else if (Object.values(identifierDeclarationOrSupervisorRequiredProfessions).includes(profession)) {
        return value ?? (!identifierDeclaration ? helpers.error('identifierDeclarationOrSupervisorRequired') : value);
    } else {
        return value !== null ? helpers.error('any.only') : value;
    }
}

export function validateSpecialismCodeField(value: string | null, helpers: Joi.CustomHelpers) {
    const profession = helpers.state.ancestors[0]?.profession as Profession | undefined;

    if (!profession) {
        return value === null ? value : helpers.error('any.invalid');
    }

    if (professionSpecialismCodes.has(profession)) {
        return value === professionSpecialismCodes.get(profession) ? value : helpers.error('any.only');
    } else if (specialismRequired.includes(profession)) {
        return value === null ? helpers.error('any.only') : value;
    } else {
        return value === null ? value : helpers.error('any.invalid');
    }
}

export function validateZorgmailNummerField(value: string | null, helpers: Joi.CustomHelpers) {
    const organizationType: OrganizationTypeWithLinkedProfessions | null = helpers.state.ancestors[1]?.organizationType;
    const profession = helpers.state.ancestors[0]?.profession as Profession;

    const zorgmailNummerRequiredProfessions = organizationType
        ? (zorgmailNummerRequiredByOrganizationType[organizationType] as Profession[])
        : zorgmailNummerRequired;

    if (Object.values(zorgmailNummerRequiredProfessions).includes(profession)) {
        const { error } = zorgMailNummerSchema.validate(value);

        return error ? helpers.error('zorgMailNumberRequired') : value;
    } else {
        const { error } = zorgMailNummerSchema.allow(null).validate(value);

        return error ? helpers.error('zorgMailNumberRequired') : value;
    }
}

export function validateIdentifierDeclarationField(value: string | null, helpers: Joi.CustomHelpers) {
    const organizationType: OrganizationTypeWithLinkedProfessions | null = helpers.state.ancestors[1]?.organizationType;
    const profession = helpers.state.ancestors[1]?.employment.profession as Profession;
    const supervisor = helpers.state.ancestors[1]?.employment?.identifierDeclarationPractitionerId;

    const identifierDeclarationRequiredProfessions = organizationType
        ? identifierDeclarationRequiredByOrganizationType[organizationType]
        : identifierDeclarationRequired;

    const identifierDeclarationOrSupervisorRequiredProfessions = organizationType
        ? (identifierDeclarationOrSupervisorRequiredByOrganizationType[organizationType] as Profession[])
        : identifierDeclarationOrSupervisorRequired;

    if (Object.values(identifierDeclarationRequiredProfessions).includes(profession)) {
        const { error } = agbCodeSchema.validate(value);

        return error ? helpers.error('identifierDeclarationRequired') : value;
    } else if (Object.values(identifierDeclarationOrSupervisorRequiredProfessions).includes(profession)) {
        const validatedValue =
            value !== null && agbCodeSchema.validate(value).error
                ? helpers.error('identifierDeclarationRequired')
                : value;

        return validatedValue ?? (!supervisor ? helpers.error('identifierDeclarationOrSupervisorRequired') : value);
    } else {
        const { error } = agbCodeSchema.allow(null).validate(value);

        return error ? helpers.error('identifierDeclarationRequired') : value;
    }
}

export function validateIdentifierRegistrationBigField(
    value: string | null,
    helpers: Joi.CustomHelpers,
    validateOnFrontend: boolean
) {
    const organizationType: OrganizationTypeWithLinkedProfessions | null = helpers.state.ancestors[1]?.organizationType;
    const profession = helpers.state.ancestors[1]?.employment.profession as Profession;

    const identifierRegistrationBigAllowedProfessions = organizationType
        ? (identifierRegistrationBigAllowedByOrganizationType[organizationType] as Profession[])
        : identifierRegistrationBigAllowed;

    if (Object.values(identifierRegistrationBigAllowedProfessions).includes(profession)) {
        if (validateOnFrontend) {
            const { error } = identifierRegistrationBigFrontendSchema.validate(value);

            return error ? helpers.error('identifierRegistrationBigNumberRequiredFrontend') : value;
        } else {
            const { error } = identifierRegistrationBigBackendSchema.validate(value);

            return error ? helpers.error('identifierRegistrationBigNumberRequiredBackend') : value;
        }
    } else {
        return value !== null ? helpers.error('any.only') : value;
    }
}

export function validateIdentifierRegistrationKpField(value: string | null, helpers: Joi.CustomHelpers) {
    const organizationType: OrganizationTypeWithLinkedProfessions | null = helpers.state.ancestors[1]?.organizationType;
    const profession = helpers.state.ancestors[1]?.employment.profession as Profession;

    const identifierRegistrationKpAllowedProfessions = organizationType
        ? identifierRegistrationKpRequiredByOrganizationType[organizationType]
        : identifierRegistrationKpAllowed;

    if (Object.values(identifierRegistrationKpAllowedProfessions).includes(profession)) {
        const { error } = identifierRegistrationKpSchema.validate(value);

        return error ? helpers.error('identifierRegistrationKpNumberRequired') : value;
    } else {
        return value !== null ? helpers.error('any.only') : value;
    }
}
